import { Box, Typography } from '@material-ui/core'
import { useEffect, useRef, useState } from 'react'

import View from '@/components/view'
import { useCreatePlantingFailure } from '@/hooks/mutations/use-create-planting-failure'
import { useGetCogUrlFromDiagnosis } from '@/hooks/queries/use-get-cog-url-from-diagnosis'
import { useGetPlantingFailureSummaryFromDiagnosis } from '@/hooks/queries/use-get-planting-failure-summary-from-diagnosis'
import { Shimmer } from '@/pages/diagnosis-view/components/shimmer'
import { Culture } from '@/types/culture'

import { PlantingFailureMap } from './planting-failure-map'
import { ControlFillLayers } from './planting-failure-map/control-fill-layers'
import { FailureLayers } from './planting-failure-map/failure-layers'
import { Summary } from './summary'

export interface PlantingFailureProps {
  culture: Culture
  treatment: string
  diagnosisId: string
}

export function PlantingFailure({
  diagnosisId,
  culture,
}: PlantingFailureProps) {
  const { data } = useGetPlantingFailureSummaryFromDiagnosis(
    culture,
    diagnosisId,
    true,
  )

  const { mutate: createPlantingFailure, isLoading: isCreating } =
    useCreatePlantingFailure()

  const hasTriggeredCreate = useRef(false)

  const { data: cogUrl } = useGetCogUrlFromDiagnosis(culture, diagnosisId)

  const [layersFillColor, setLayersFillColor] = useState<
    Record<string, boolean>
  >({})

  useEffect(() => {
    if (data && !data.failures && !isCreating && !hasTriggeredCreate.current) {
      hasTriggeredCreate.current = true
      createPlantingFailure({ culture, demandId: diagnosisId })
    }
  }, [data, culture, diagnosisId, createPlantingFailure, isCreating])

  useEffect(() => {
    if (data?.failures) {
      setLayersFillColor((prev) => {
        const next = { ...prev }
        data.failures.forEach((failure) => {
          if (next[failure.layer] === undefined) {
            next[failure.layer] = true
          }
        })
        return next
      })
    }
  }, [data?.failures])

  const handleLayerChange = (layerName: string, checked: boolean) => {
    setLayersFillColor((prev) => ({
      ...prev,
      [layerName]: checked,
    }))
  }

  const failureLayersState = data?.failures?.map((f) => ({
    name: f.layer,
    color: f.color,
    fillColor: !!layersFillColor[f.layer],
  }))

  return (
    <View
      padding='XL'
      overflow
      aside={<Summary diagnosisId={diagnosisId} culture={culture} />}
      section={
        <>
          {!data || isCreating ? (
            <Shimmer />
          ) : !data.failures ? (
            <Box width={290} textAlign='center'>
              <Typography variant='subtitle1'>
                Visualização do diagnóstico de Falha de Plantio estará
                disponível em breve.
              </Typography>
            </Box>
          ) : (
            <PlantingFailureMap
              perimeter={data.perimeter}
              mapLabel='Mapa de falhas de plantio'
              cogUrl={cogUrl}
            >
              <ControlFillLayers
                layers={failureLayersState || []}
                handleLayerChange={handleLayerChange}
              />
              <FailureLayers
                failures={data.failures}
                layersFillColor={layersFillColor}
              />
            </PlantingFailureMap>
          )}
        </>
      }
    />
  )
}
